import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  Skeleton,
  Tooltip,
  Fade,
  Badge,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from '@mui/material';
import {
  GridView,
  Search,
  Close,
  PlayArrow,
  FilterList,
  Sort,
  ViewModule,
  ViewList,
  Refresh,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
// Commenting out react-window for now - using simpler grid
// import { FixedSizeGrid as Grid as VirtualGrid } from 'react-window';

const GalleryCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #1A1A1A 0%, #2A2A2A 100%)'
    : 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
  border: `1px solid ${theme.palette.mode === 'dark' ? '#333333' : '#E0E0E0'}`,
  borderRadius: 12,
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 20px rgba(0, 0, 0, 0.4)'
    : '0 4px 20px rgba(0, 0, 0, 0.08)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 16px 48px rgba(0, 0, 0, 0.6)'
      : '0 16px 48px rgba(0, 0, 0, 0.15)',
    border: `1px solid ${theme.palette.mode === 'dark' ? '#FF6B6B' : '#FF5252'}`,
    '& .thumbnail': {
      transform: 'scale(1.05)',
    },
    '& .play-overlay': {
      opacity: 1,
    },
  },
}));

const ThumbnailContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  height: 180,
  '& .thumbnail': {
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '& .play-overlay': {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '& .MuiSvgIcon-root': {
      fontSize: '3rem',
      color: '#FFFFFF',
      filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))',
    },
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #2A2A2A 0%, #1A1A1A 100%)'
    : 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)',
  border: `1px solid ${theme.palette.mode === 'dark' ? '#333333' : '#E0E0E0'}`,
  borderRadius: 12,
  padding: 20,
  marginBottom: 20,
  display: 'flex',
  gap: 16,
  alignItems: 'center',
  flexWrap: 'wrap',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 20px rgba(0, 0, 0, 0.3)'
    : '0 4px 20px rgba(0, 0, 0, 0.05)',
}));

const VideoGallery = ({ 
  allVideoIds, 
  onPlayVideo, 
  currentVideoId,
  videoStats 
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEra, setSelectedEra] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // YouTube thumbnail URL generator
  const getThumbnailUrl = (videoId, quality = 'mqdefault') => {
    return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
  };

  // Parse YouTube duration format
  const parseDuration = (duration) => {
    if (!duration) return 'Unknown';
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return 'Unknown';
    const hours = parseInt(match[1] || 0);
    const minutes = parseInt(match[2] || 0);
    const seconds = parseInt(match[3] || 0);
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  };

  // Load video data with metadata
  const loadVideoData = useCallback(async () => {
    setLoading(true);
    try {
      const { getAllVideoData } = await import('../data/videoData');
      const videoData = await getAllVideoData();
      
      const videosWithThumbnails = videoData.map(video => ({
        ...video,
        thumbnailUrl: getThumbnailUrl(video.id),
        highQualityThumbnail: getThumbnailUrl(video.id, 'hqdefault'),
        duration: parseDuration(video.duration),
        publishedDate: new Date(video.publishedAt),
      }));

      setVideos(videosWithThumbnails);
      setFilteredVideos(videosWithThumbnails);
    } catch (error) {
      console.error('Error loading video data:', error);
      // Fallback to basic video IDs
      const basicVideos = allVideoIds.map(id => ({
        id,
        title: 'David Dobrik Vlog',
        thumbnailUrl: getThumbnailUrl(id),
        era: 'Unknown',
        duration: 'Unknown',
        publishedDate: new Date(),
      }));
      setVideos(basicVideos);
      setFilteredVideos(basicVideos);
    } finally {
      setLoading(false);
    }
  }, [allVideoIds]);

  // Filter and sort videos
  const processVideos = useCallback(() => {
    let processed = [...videos];

    // Apply search filter
    if (searchTerm) {
      processed = processed.filter(video =>
        video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        video.era.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply era filter
    if (selectedEra) {
      processed = processed.filter(video => video.era === selectedEra);
    }

    // Apply sorting
    switch (sortBy) {
      case 'newest':
        processed.sort((a, b) => b.publishedDate - a.publishedDate);
        break;
      case 'oldest':
        processed.sort((a, b) => a.publishedDate - b.publishedDate);
        break;
      case 'title':
        processed.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'era':
        processed.sort((a, b) => a.era.localeCompare(b.era));
        break;
      default:
        break;
    }

    setFilteredVideos(processed);
  }, [videos, searchTerm, selectedEra, sortBy]);

  // Get unique eras for filter
  const eras = useMemo(() => {
    const uniqueEras = [...new Set(videos.map(video => video.era))];
    return uniqueEras.filter(era => era && era !== 'Unknown');
  }, [videos]);

  // Load data when component mounts or video IDs change
  useEffect(() => {
    if (allVideoIds.length > 0 && open) {
      loadVideoData();
    }
  }, [allVideoIds, open, loadVideoData]);

  // Process videos when filters change
  useEffect(() => {
    processVideos();
  }, [processVideos]);

  // Handle video selection
  const handleVideoSelect = (video) => {
    onPlayVideo(video.id);
    setOpen(false);
  };

  // Video card component removed - using inline cards in the grid instead

  return (
    <>
      {/* Gallery Trigger Button */}
      <Tooltip title="Browse All Videos" arrow>
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            bgcolor: 'rgba(76,175,80,0.2)',
            border: '2px solid rgba(76,175,80,0.5)',
            borderRadius: 2,
            color: theme.palette.text.primary,
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: 'rgba(76,175,80,0.4)',
              transform: 'scale(1.1)',
              borderColor: '#4CAF50',
            }
          }}
        >
          <Badge badgeContent={filteredVideos.length} color="success">
            <GridView />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Video Gallery Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: (theme) => ({
            width: '95vw',
            height: '90vh',
            maxWidth: 'none',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%)'
              : 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 5,
            color: theme.palette.text.primary,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 24px 64px rgba(0, 0, 0, 0.8)'
              : '0 24px 64px rgba(0, 0, 0, 0.15)',
          })
        }}
      >
        <DialogTitle sx={(theme) => ({ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #2A2A2A 0%, #1A1A1A 100%)'
            : 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)',
          color: theme.palette.text.primary,
          fontWeight: 600,
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 3
        })}>
          <Box display="flex" alignItems="center" gap={2}>
            <GridView sx={(theme) => ({ 
              color: theme.palette.mode === 'dark' ? '#C0C0C0' : '#9E9E9E' 
            })} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Video Gallery
            </Typography>
            <Chip 
              label={`${filteredVideos.length} videos`} 
              sx={(theme) => ({
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #FF6B6B 0%, #FF5252 100%)'
                  : 'linear-gradient(135deg, #FF5252 0%, #E53935 100%)',
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: '0.75rem'
              })}
            />
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh">
              <IconButton 
                onClick={loadVideoData} 
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.04)',
                  }
                })}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <IconButton 
              onClick={() => setOpen(false)} 
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.04)',
                }
              })}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Search and Filter Controls */}
          <SearchContainer>
            <TextField
              placeholder="Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={(theme) => ({ 
                      color: theme.palette.text.secondary 
                    })} />
                  </InputAdornment>
                ),
              }}
              sx={(theme) => ({
                flex: 1,
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  color: theme.palette.text.primary,
                  '& fieldset': { 
                    borderColor: theme.palette.divider 
                  },
                  '&:hover fieldset': { 
                    borderColor: theme.palette.text.secondary 
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: theme.palette.secondary.main 
                  },
                },
              })}
            />
            
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel sx={(theme) => ({ 
                color: theme.palette.text.secondary 
              })}>Era</InputLabel>
              <Select
                value={selectedEra}
                label="Era"
                onChange={(e) => setSelectedEra(e.target.value)}
                sx={(theme) => ({
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': { 
                    borderColor: theme.palette.divider 
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': { 
                    borderColor: theme.palette.text.secondary 
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                    borderColor: theme.palette.secondary.main 
                  },
                })}
              >
                <MenuItem value="">All Eras</MenuItem>
                {eras.map(era => (
                  <MenuItem key={era} value={era}>{era}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel sx={(theme) => ({ 
                color: theme.palette.text.secondary 
              })}>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
                sx={(theme) => ({
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': { 
                    borderColor: theme.palette.divider 
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': { 
                    borderColor: theme.palette.text.secondary 
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                    borderColor: theme.palette.secondary.main 
                  },
                })}
              >
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="oldest">Oldest First</MenuItem>
                <MenuItem value="title">Title A-Z</MenuItem>
                <MenuItem value="era">Era</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setSelectedEra('');
                setSortBy('newest');
              }}
              sx={(theme) => ({
                color: theme.palette.text.primary,
                borderColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.3)' 
                  : 'rgba(0,0,0,0.2)',
                '&:hover': { 
                  borderColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.5)' 
                    : 'rgba(0,0,0,0.3)' 
                }
              })}
            >
              Clear Filters
            </Button>
          </SearchContainer>

          {/* Video Grid */}
          <Box flex={1} position="relative" sx={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}>
            {loading ? (
              <Grid container spacing={2}>
                {Array.from({ length: 12 }).map((_, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}>
                      <Skeleton variant="rectangular" height={180} />
                      <CardContent>
                        <Skeleton variant="text" />
                        <Skeleton variant="text" width="60%" />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : filteredVideos.length > 0 ? (
              <Grid container spacing={2}>
                {filteredVideos.map((video) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
                    <GalleryCard onClick={() => handleVideoSelect(video)}>
                      <ThumbnailContainer>
                        <CardMedia
                          component="img"
                          image={video.thumbnailUrl}
                          alt={video.title}
                          className="thumbnail"
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            // Fallback to lower quality thumbnail
                            e.target.src = getThumbnailUrl(video.id, 'default');
                          }}
                        />
                        <Box className="play-overlay">
                          <PlayArrow sx={{ fontSize: 48, color: '#FFFFFF' }} />
                        </Box>
                        
                        {/* Duration badge */}
                        {video.duration !== 'Unknown' && (
                          <Chip
                            label={video.duration}
                            size="small"
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              right: 8,
                              bgcolor: 'rgba(0,0,0,0.8)',
                              color: '#FFFFFF',
                              fontWeight: 600,
                            }}
                          />
                        )}
                        
                        {/* Current video indicator */}
                        {video.id === currentVideoId && (
                          <Chip
                            label="NOW PLAYING"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              bgcolor: '#FF0050',
                              color: '#FFFFFF',
                              fontWeight: 800,
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.7 },
                                '100%': { opacity: 1 },
                              },
                            }}
                          />
                        )}
                      </ThumbnailContainer>
                      
                      <CardContent sx={{ p: 2 }}>
                        <Typography
                          variant="subtitle2"
                          color={theme.palette.text.primary}
                          fontWeight={600}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.3,
                            height: '2.6em',
                            mb: 1,
                          }}
                        >
                          {video.title}
                        </Typography>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Chip
                            label={video.era}
                            size="small"
                            variant="outlined"
                            sx={(theme) => ({
                              color: theme.palette.text.secondary,
                              borderColor: theme.palette.mode === 'dark' 
                                ? 'rgba(255,255,255,0.3)' 
                                : 'rgba(0,0,0,0.2)',
                              fontSize: '0.75rem',
                            })}
                          />
                          <Typography variant="caption" sx={(theme) => ({ 
                            color: theme.palette.text.secondary 
                          })}>
                            {video.publishedDate.getFullYear()}
                          </Typography>
                        </Box>
                      </CardContent>
                    </GalleryCard>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="400px"
                sx={(theme) => ({ color: theme.palette.text.primary })}
              >
                <GridView sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No videos found
                </Typography>
                <Typography variant="body2" sx={(theme) => ({ 
                  color: theme.palette.text.secondary 
                })}>
                  Try adjusting your search or filter criteria
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VideoGallery;